// frontend/src/pages/Admin/RGPDValidation.tsx
// Dashboard de validation humaine des fiches RGPD enrichies par l'agent IA

import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FicheSummary {
  id:                   string;
  judilibre_id:         string;
  jurisdiction:         string;
  chamber:              string;
  decision_date:        string;
  solution:             string;
  rgpd_agent_validated: boolean;
  rgpd_agent_model:     string | null;
  rgpd_agent_timestamp: string | null;
  rgpd_score_confiance: number | null;
  resume_rgpd:          string | null;
  mentionne_cnil:       boolean;
  mentionne_cjue:       boolean;
  n_citations_cnil:     number;
  n_citations_cjue:     number;
  n_articles_rgpd:      number;
  n_problemes:          number;
  human_validated:      boolean;
  human_validated_by:   string | null;
  human_validated_at:   string | null;
  human_comment:        string | null;
}

interface FicheDetail extends FicheSummary {
  rgpd_citations_cnil:  string[];
  rgpd_citations_cjue:  string[];
  rgpd_articles_rgpd:   string[];
  rgpd_reglements_eu:   string[];
  rgpd_articles_lil:    string[];
  rgpd_cnil_links:      any[];
  rgpd_cjue_links:      any[];
  implication_cnil:     string | null;
  rgpd_problemes:       string[];
  rgpd_jury_flash:      { score: number; impression: string; points_forts: string[]; points_faibles: string[] } | null;
  rgpd_jury_sonnet:     { score: number; impression: string; points_forts: string[]; points_faibles: string[] } | null;
  probleme:             string | null;
  solution_text:        string | null;
  faits:                string | null;
  procedure:            string | null;
  ecli:                 string | null;
  number:               string | null;
  themes:               string[];
  mots_cles:            string[];
}

interface Stats {
  total_fiches_rgpd:   number;
  agent_validated:     number;
  agent_pending:       number;
  human_validated:     number;
  human_pending:       number;
  with_resume:         number;
  with_problems:       number;
  low_score:           number;
  no_score:            number;
  avg_score:           number | null;
  score_distribution:  Record<string, number>;
  top_problems:        { probleme: string; count: number }[];
  pipeline_progress:   { pct_agent: number; pct_human: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(iso));
  } catch { return iso; }
};

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const ScoreBadge = ({ score }: { score: number | null }) => {
  if (score === null || score === undefined) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">—</span>;
  }
  const pct  = Math.round(score * 100);
  const cls  = score >= 0.9
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : score >= 0.7
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>{pct}%</span>;
};

const BoolBadge = ({ value, labelTrue, labelFalse }:
  { value: boolean; labelTrue?: string; labelFalse?: string }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
    value
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      : 'bg-muted text-muted-foreground'
  }`}>
    {value ? (labelTrue || 'oui') : (labelFalse || 'non')}
  </span>
);

const StatCard = ({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-2xl font-bold ${color || 'text-foreground'}`}>{value}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </div>
);

// ── Modal Détail Fiche ────────────────────────────────────────────────────────

const FicheModal = ({
  fiche, onClose, onValidate, onInvalidate, apiClient, userName
}: {
  fiche: FicheDetail;
  onClose: () => void;
  onValidate: (id: string) => void;
  onInvalidate: (id: string) => void;
  apiClient: any;
  userName: string;
}) => {
  const [validating,  setValidating]  = useState(false);
  const [comment,     setComment]     = useState(fiche.human_comment || '');
  const [savingComment, setSavingComment] = useState(false);
  const [commentSaved,  setCommentSaved]  = useState(false);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/rgpd-admin/fiches/${encodeURIComponent(fiche.judilibre_id)}/validate`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ validated_by: userName, comment: comment || null }),
        }
      );
      if (resp.ok) {
        onValidate(fiche.judilibre_id);
        onClose();
      }
    } finally {
      setValidating(false);
    }
  };

  const handleInvalidate = async () => {
    setValidating(true);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/rgpd-admin/fiches/${encodeURIComponent(fiche.judilibre_id)}/invalidate`),
        { method: 'POST' }
      );
      if (resp.ok) {
        onInvalidate(fiche.judilibre_id);
        onClose();
      }
    } finally {
      setValidating(false);
    }
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await fetch(
        apiClient.buildEndpoint(`/api/rgpd-admin/fiches/${encodeURIComponent(fiche.judilibre_id)}/comment`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ comment }),
        }
      );
      setCommentSaved(true);
      setTimeout(() => setCommentSaved(false), 2000);
    } finally {
      setSavingComment(false);
    }
  };

  const JuryBlock = ({ label, jury }: { label: string; jury: FicheDetail['rgpd_jury_flash'] }) => {
    if (!jury) return null;
    return (
      <div className="border border-border rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
          <ScoreBadge score={jury.score} />
        </div>
        <p className="text-sm text-foreground">{jury.impression}</p>
        {jury.points_forts?.length > 0 && (
          <div>
            <span className="text-xs text-emerald-600 font-medium">Points forts : </span>
            <span className="text-xs text-muted-foreground">{jury.points_forts.join(' · ')}</span>
          </div>
        )}
        {jury.points_faibles?.length > 0 && (
          <div>
            <span className="text-xs text-rose-600 font-medium">Points faibles : </span>
            <span className="text-xs text-muted-foreground">{jury.points_faibles.join(' · ')}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-background border border-border rounded-xl w-full max-w-4xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <ScoreBadge score={fiche.rgpd_score_confiance} />
              <BoolBadge value={fiche.rgpd_agent_validated} labelTrue="Agent ✓" labelFalse="Agent —" />
              <BoolBadge value={fiche.human_validated} labelTrue="Humain ✓" labelFalse="À valider" />
              {fiche.mentionne_cnil && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium">CNIL</span>}
              {fiche.mentionne_cjue && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">CJUE</span>}
            </div>
            <h2 className="text-base font-semibold mt-2 text-foreground leading-snug">
              {fiche.jurisdiction} · {fiche.chamber}
            </h2>
            <p className="text-sm text-muted-foreground">{fmtDate(fiche.decision_date)} · {fiche.number || fiche.ecli || fiche.judilibre_id}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none ml-4 flex-shrink-0">✕</button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">

          {/* Résumé RGPD */}
          {fiche.resume_rgpd && (
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-2">Résumé RGPD</h3>
              <p className="text-sm text-foreground leading-relaxed bg-muted/40 rounded-lg p-4">
                {fiche.resume_rgpd}
              </p>
            </section>
          )}

          {/* Citations validées */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Citations CNIL ({fiche.rgpd_citations_cnil.length})</h4>
              {fiche.rgpd_citations_cnil.length > 0
                ? fiche.rgpd_citations_cnil.map(c => (
                    <div key={c} className="text-sm text-foreground bg-purple-50 dark:bg-purple-900/10 rounded px-2 py-1 mb-1">{c}</div>
                  ))
                : <span className="text-xs text-muted-foreground">Aucune</span>}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Citations CJUE ({fiche.rgpd_citations_cjue.length})</h4>
              {fiche.rgpd_citations_cjue.length > 0
                ? fiche.rgpd_citations_cjue.map(c => (
                    <div key={c} className="text-sm text-foreground bg-amber-50 dark:bg-amber-900/10 rounded px-2 py-1 mb-1">{c}</div>
                  ))
                : <span className="text-xs text-muted-foreground">Aucune</span>}
            </div>
          </section>

          {/* Articles RGPD + LIL */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Articles RGPD 2016/679 ({fiche.rgpd_articles_rgpd.length})</h4>
              {fiche.rgpd_articles_rgpd.length > 0
                ? <div className="flex flex-wrap gap-1">
                    {fiche.rgpd_articles_rgpd.map(a => (
                      <span key={a} className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">Art. {a}</span>
                    ))}
                  </div>
                : <span className="text-xs text-muted-foreground">Aucun</span>}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Articles LIL n°78-17 ({(fiche.rgpd_articles_lil || []).length})</h4>
              {(fiche.rgpd_articles_lil || []).length > 0
                ? <div className="flex flex-wrap gap-1">
                    {fiche.rgpd_articles_lil.map(a => (
                      <span key={a} className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">Art. {a}</span>
                    ))}
                  </div>
                : <span className="text-xs text-muted-foreground">Aucun</span>}
            </div>
          </section>

          {/* Implication CNIL */}
          {fiche.implication_cnil && (
            <section>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Implication CNIL</h4>
              <p className="text-sm text-foreground bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3">{fiche.implication_cnil}</p>
            </section>
          )}

          {/* Problèmes détectés */}
          {fiche.rgpd_problemes?.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold text-rose-600 uppercase mb-2">⚠ Problèmes détectés ({fiche.rgpd_problemes.length})</h4>
              <ul className="space-y-1">
                {fiche.rgpd_problemes.map((p, i) => (
                  <li key={i} className="text-sm text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/10 rounded px-3 py-1.5">{p}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Jury IA */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-3">Évaluation par le jury IA</h3>
            <div className="space-y-3">
              <JuryBlock label="DeepSeek Flash" jury={fiche.rgpd_jury_flash} />
              <JuryBlock label="Claude Sonnet" jury={fiche.rgpd_jury_sonnet} />
              {!fiche.rgpd_jury_flash && !fiche.rgpd_jury_sonnet && (
                <p className="text-sm text-muted-foreground italic">Jury non encore calculé.</p>
              )}
            </div>
          </section>

          {/* Commentaire humain */}
          <section>
            <h3 className="text-sm font-semibold text-foreground mb-2">Commentaire admin</h3>
            <textarea
              value={comment}
              onChange={e => { setComment(e.target.value); setCommentSaved(false); }}
              placeholder="Observations, corrections, points à revoir…"
              rows={3}
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            <div className="flex items-center justify-end gap-3 mt-2">
              {commentSaved && <span className="text-xs text-emerald-600">✓ Commentaire enregistré</span>}
              <button
                onClick={handleSaveComment}
                disabled={savingComment}
                className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              >
                {savingComment ? '…' : 'Enregistrer le commentaire'}
              </button>
            </div>
          </section>

          {/* Méta agent */}
          <section className="text-xs text-muted-foreground border-t border-border pt-4 flex flex-wrap gap-4">
            <span>Modèle : {fiche.rgpd_agent_model || '—'}</span>
            <span>Traité le : {fmtDateTime(fiche.rgpd_agent_timestamp)}</span>
            {fiche.human_validated && (
              <span className="text-emerald-600">✓ Validé par {fiche.human_validated_by} le {fmtDateTime(fiche.human_validated_at)}</span>
            )}
          </section>
        </div>

        {/* Footer — Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">
            Fermer
          </button>
          <div className="flex gap-3">
            {fiche.human_validated && (
              <button
                onClick={handleInvalidate}
                disabled={validating}
                className="text-sm px-4 py-2 rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50"
              >
                Annuler validation
              </button>
            )}
            {!fiche.human_validated && (
              <button
                onClick={handleValidate}
                disabled={validating || !fiche.rgpd_agent_validated}
                title={!fiche.rgpd_agent_validated ? "L'agent doit d'abord traiter cette fiche" : undefined}
                className="text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {validating ? '...' : '✓ Valider cette fiche'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const RGPDValidationContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate  = useNavigate();

  const [stats,        setStats]        = useState<Stats | null>(null);
  const [fiches,       setFiches]       = useState<FicheSummary[]>([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error,        setError]        = useState('');
  const [detail,       setDetail]       = useState<FicheDetail | null>(null);
  const [loadingDetail,setLoadingDetail]= useState(false);

  // Filtres
  const [filterAgent,    setFilterAgent]    = useState<string>('');   // '' | 'true' | 'false'
  const [filterHuman,    setFilterHuman]    = useState<string>('');
  const [filterProblems, setFilterProblems] = useState<string>('');
  const [filterCnil,     setFilterCnil]     = useState<string>('');
  const [filterScoreMin, setFilterScoreMin] = useState<string>('');
  const [filterSearch,   setFilterSearch]   = useState('');
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  // Chargement stats
  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    setLoadingStats(true);
    fetch(apiClient.buildEndpoint('/api/rgpd-admin/stats'))
      .then(r => r.json())
      .then(d => { setStats(d); setLoadingStats(false); })
      .catch(() => setLoadingStats(false));
  }, [isAuthenticated, isReady]);

  // Chargement liste
  const loadFiches = useCallback(() => {
    if (!isAuthenticated || !isReady) return;
    setLoading(true);
    setError('');

    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
    });
    if (filterAgent)    params.set('agent_validated', filterAgent);
    if (filterHuman)    params.set('human_validated', filterHuman);
    if (filterProblems) params.set('has_problems', filterProblems);
    if (filterCnil)     params.set('mentionne_cnil', filterCnil);
    if (filterScoreMin) params.set('score_min', filterScoreMin);
    if (filterSearch)   params.set('search_id', filterSearch);

    fetch(apiClient.buildEndpoint(`/api/rgpd-admin/fiches?${params}`))
      .then(r => { if (!r.ok) throw new Error('Erreur'); return r.json(); })
      .then(d => {
        // Support both "items" (new) and "fiches" (legacy) key names
        setFiches(d.items ?? d.fiches ?? []);
        setTotal(d.total ?? d.count ?? 0);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [isAuthenticated, isReady, page, filterAgent, filterHuman, filterProblems, filterCnil, filterScoreMin, filterSearch]);

  useEffect(() => { loadFiches(); }, [loadFiches]);

  const openDetail = async (judilibre_id: string) => {
    setLoadingDetail(true);
    try {
      const resp = await fetch(apiClient.buildEndpoint(`/api/rgpd-admin/fiches/${encodeURIComponent(judilibre_id)}`));
      if (!resp.ok) throw new Error('Erreur');
      setDetail(await resp.json());
    } catch { /* ignore */ }
    setLoadingDetail(false);
  };

  const handleValidated = (id: string) => {
    setFiches(f => f.map(x => x.judilibre_id === id ? { ...x, human_validated: true } : x));
    setStats(s => s ? { ...s, human_validated: s.human_validated + 1, human_pending: s.human_pending - 1 } : s);
    loadFiches();
  };
  const handleInvalidated = (id: string) => {
    setFiches(f => f.map(x => x.judilibre_id === id ? { ...x, human_validated: false } : x));
    loadFiches();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const FilterSelect = ({ label, value, onChange, options }: {
    label: string; value: string; onChange: (v: string) => void;
    options: { value: string; label: string }[];
  }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={e => { onChange(e.target.value); setPage(1); }}
        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto w-full h-full overflow-y-auto [&>*]:shrink-0">
      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Validation RGPD</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pipeline de validation humaine des fiches jurisprudence enrichies par l'agent IA
        </p>
      </div>

      {/* Stats */}
      {loadingStats
        ? <div className="flex gap-2"><Loader /></div>
        : stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <StatCard label="Fiches RGPD" value={stats.total_fiches_rgpd} />
              <StatCard
                label="Traitées par agent"
                value={stats.agent_validated}
                sub={`${stats.pipeline_progress.pct_agent}% — ${stats.agent_pending} en attente`}
                color="text-blue-600"
              />
              <StatCard
                label="Validées (humain)"
                value={stats.human_validated}
                sub={`${stats.pipeline_progress.pct_human}% — ${stats.human_pending} à valider`}
                color="text-emerald-600"
              />
              <StatCard
                label="Avec problèmes"
                value={stats.with_problems}
                color={stats.with_problems > 0 ? 'text-rose-600' : 'text-foreground'}
              />
              <StatCard
                label="Score moyen"
                value={stats.avg_score !== null ? `${Math.round((stats.avg_score ?? 0) * 100)}%` : '—'}
                sub={`Score < 70% : ${stats.low_score}`}
              />
              <StatCard
                label="Avec résumé"
                value={stats.with_resume}
                color="text-purple-600"
              />
            </div>

            {/* Barre de progression */}
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Traitement agent</span>
                  <span>{stats.agent_validated}/{stats.total_fiches_rgpd}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${stats.pipeline_progress.pct_agent}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Validation humaine</span>
                  <span>{stats.human_validated}/{stats.total_fiches_rgpd}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${stats.pipeline_progress.pct_human}%` }} />
                </div>
              </div>
            </div>

            {/* Distribution scores */}
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(stats.score_distribution).map(([range, count]) => (
                <div key={range} className="bg-card border border-border rounded-lg p-3 text-center">
                  <div className="text-xs text-muted-foreground mb-1">{range}</div>
                  <div className="text-lg font-bold text-foreground">{count}</div>
                </div>
              ))}
            </div>
          </>
        )}

      {/* Filtres */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <FilterSelect
          label="Agent validé"
          value={filterAgent}
          onChange={setFilterAgent}
          options={[{ value: '', label: 'Tous' }, { value: 'true', label: 'Oui ✓' }, { value: 'false', label: 'Non ✗' }]}
        />
        <FilterSelect
          label="Humain validé"
          value={filterHuman}
          onChange={setFilterHuman}
          options={[{ value: '', label: 'Tous' }, { value: 'true', label: 'Oui ✓' }, { value: 'false', label: 'À valider' }]}
        />
        <FilterSelect
          label="Problèmes"
          value={filterProblems}
          onChange={setFilterProblems}
          options={[{ value: '', label: 'Tous' }, { value: 'true', label: 'Avec problèmes' }, { value: 'false', label: 'Sans problème' }]}
        />
        <FilterSelect
          label="Mentionne CNIL"
          value={filterCnil}
          onChange={setFilterCnil}
          options={[{ value: '', label: 'Tous' }, { value: 'true', label: 'Oui' }, { value: 'false', label: 'Non' }]}
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Score min</label>
          <select
            value={filterScoreMin}
            onChange={e => { setFilterScoreMin(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Tous</option>
            <option value="0.9">≥ 90%</option>
            <option value="0.7">≥ 70%</option>
            <option value="0.5">≥ 50%</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Recherche ID</label>
          <input
            type="text"
            value={filterSearch}
            onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
            placeholder="judilibre_id..."
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          onClick={() => { setFilterAgent(''); setFilterHuman(''); setFilterProblems(''); setFilterCnil(''); setFilterScoreMin(''); setFilterSearch(''); setPage(1); }}
          className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 border border-border rounded-lg"
        >
          Réinitialiser
        </button>
      </div>

      {/* Tableau */}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {loading
        ? <div className="flex justify-center py-12"><Loader /></div>
        : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm text-muted-foreground">
              {total} fiche{total > 1 ? 's' : ''} · page {page}/{totalPages || 1}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Décision</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Score</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">CNIL</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Cit.</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">⚠</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Agent</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Humain</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {fiches.map(f => (
                    <tr key={f.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 max-w-xs">
                        <div className="font-medium text-foreground truncate">{f.jurisdiction || f.judilibre_id}</div>
                        <div className="text-xs text-muted-foreground truncate">{f.chamber || f.solution}</div>
                        {f.resume_rgpd && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5 italic">{f.resume_rgpd.slice(0, 80)}…</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(f.decision_date)}</td>
                      <td className="px-3 py-3 text-center"><ScoreBadge score={f.rgpd_score_confiance} /></td>
                      <td className="px-3 py-3 text-center">
                        {f.mentionne_cnil && <span className="text-xs text-purple-600 font-semibold">CNIL</span>}
                      </td>
                      <td className="px-3 py-3 text-center text-muted-foreground">
                        {f.n_citations_cnil > 0 && <span className="text-purple-600">{f.n_citations_cnil}C</span>}
                        {f.n_citations_cjue > 0 && <span className="text-amber-600 ml-1">{f.n_citations_cjue}J</span>}
                        {f.n_articles_rgpd > 0 && <span className="text-emerald-600 ml-1">{f.n_articles_rgpd}A</span>}
                        {f.n_citations_cnil === 0 && f.n_citations_cjue === 0 && f.n_articles_rgpd === 0 && '—'}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {f.n_problemes > 0
                          ? <span className="text-xs text-rose-600 font-semibold">{f.n_problemes}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <BoolBadge value={f.rgpd_agent_validated} labelTrue="✓" labelFalse="—" />
                      </td>
                      <td className="px-3 py-3 text-center">
                        {f.human_validated
                          ? <span className="text-xs text-emerald-600 font-semibold">✓ {f.human_validated_by}</span>
                          : <span className="text-xs text-muted-foreground">—</span>}
                        {f.human_comment && (
                          <span className="ml-1 text-xs text-blue-500" title={f.human_comment}>💬</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => openDetail(f.judilibre_id)}
                          className="text-xs px-3 py-1 rounded-lg bg-muted hover:bg-muted/70 text-foreground transition-colors whitespace-nowrap"
                        >
                          {loadingDetail ? '…' : 'Détail →'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {fiches.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center py-12 text-muted-foreground">
                        Aucune fiche correspondant aux filtres.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 p-4 border-t border-border">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  ← Précédent
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  Suivant →
                </button>
              </div>
            )}
          </div>
        )}

      {/* Modal détail */}
      {detail && (
        <FicheModal
          fiche={detail}
          onClose={() => setDetail(null)}
          onValidate={handleValidated}
          onInvalidate={handleInvalidated}
          apiClient={apiClient}
          userName={user?.identifier || 'admin'}
        />
      )}
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

const RGPDValidation = () => (
  <Page>
    <RGPDValidationContent />
  </Page>
);

export default RGPDValidation;
